import type { CommerceProductKind, CommerceProductView } from 'shared';

export const productKindOptions: Array<{ value: CommerceProductKind; label: string }> = [
  { value: 'pro_plan', label: 'Pro 会员' },
  { value: 'team_plan', label: 'Team 会员' },
  { value: 'team_seat_pack', label: 'Team 席位包' },
  { value: 'credit_pack', label: '积点包（Credits）' },
  { value: 'storage_pack', label: '存储扩容包' },
  { value: 'record_pack', label: '数据记录扩容包' },
];

export function productKindLabel(kind: CommerceProductKind): string {
  return productKindOptions.find((item) => item.value === kind)?.label ?? '未知商品类型';
}

export const numberLabel = (value: number) => value.toLocaleString('zh-CN');
export const storageLabel = (bytes: number) => `${numberLabel(bytes / 1024 ** 3)} GiB`;

export function productPrice(product: CommerceProductView): string {
  const amount = new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: product.currency,
    minimumFractionDigits: 2,
  }).format(product.amountMinor / 100);
  return product.purchaseMode === 'auto_renew'
    ? `${amount} / ${product.billingCycle === 'year' ? '年' : '月'}`
    : amount;
}

export function purchaseModeLabel(product: CommerceProductView): string {
  return product.purchaseMode === 'auto_renew'
    ? `${product.billingCycle === 'year' ? '按年' : '按月'}自动续费`
    : '一次性购买';
}

export function productValidity(product: CommerceProductView): string {
  if (product.kind === 'team_seat_pack') return '随 Team 会员到期';
  const days =
    product.kind === 'credit_pack'
      ? (product.grant.creditsExpiryDays ?? 365)
      : product.grant.durationDays;
  return days ? `${numberLabel(days)} 天` : '未配置';
}

export function productGrantSummary(product: CommerceProductView): string {
  const grant = product.grant;
  switch (product.kind) {
    case 'pro_plan':
      return `Pro 权益 · ${grant.capacity?.maxWorkspaces ?? '—'} 个工作台`;
    case 'team_plan':
      return `Team 权益 · ${numberLabel(grant.seatCount ?? 0)} 席`;
    case 'team_seat_pack':
      return `增加 ${numberLabel(grant.seatCount ?? 0)} 个团队席位`;
    case 'credit_pack':
      return `到账 ${numberLabel(grant.credits ?? 0)} 积点`;
    case 'storage_pack':
      return `增加 ${storageLabel(grant.storageBytes ?? 0)} 存储`;
    case 'record_pack':
      return `增加 ${numberLabel(grant.recordCount ?? 0)} 行数据容量`;
    default:
      return '请检查商品权益';
  }
}

export function suggestedProductName(product: CommerceProductView): string {
  const grant = product.grant;
  const period =
    product.purchaseMode === 'auto_renew' ? purchaseModeLabel(product) : productValidity(product);
  switch (product.kind) {
    case 'pro_plan':
      return `Pro 会员 · ${period}`;
    case 'team_plan':
      return `Team 会员 · ${numberLabel(grant.seatCount ?? 0)} 席 · ${period}`;
    case 'team_seat_pack':
      return `Team 席位包 · ${numberLabel(grant.seatCount ?? 0)} 席`;
    case 'credit_pack':
      return `${numberLabel(grant.credits ?? 0)} 积点包 · ${period}`;
    case 'storage_pack':
      return `存储扩容包 · ${storageLabel(grant.storageBytes ?? 0)} · ${period}`;
    case 'record_pack':
      return `数据记录扩容包 · ${numberLabel(grant.recordCount ?? 0)} 行 · ${period}`;
    default:
      return '';
  }
}

export function compareProducts(left: CommerceProductView, right: CommerceProductView): number {
  const index = (kind: CommerceProductKind) =>
    productKindOptions.findIndex((item) => item.value === kind);
  return (
    index(left.kind) - index(right.kind) ||
    left.displayOrder - right.displayOrder ||
    left.amountMinor - right.amountMinor ||
    left.id.localeCompare(right.id)
  );
}

export function matchesProduct(product: CommerceProductView, query: string): boolean {
  return `${product.name} ${product.id} ${productKindLabel(product.kind)}`
    .toLocaleLowerCase()
    .includes(query.trim().toLocaleLowerCase());
}

export function canRedeemProduct(product: CommerceProductView): boolean {
  return product.enabled && product.purchaseMode !== 'auto_renew';
}

export type ProductInput = Omit<CommerceProductView, 'id' | 'currency'> & { id?: string };
